import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
  LinearProgress,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  AddCircleOutline as AddStepIcon
} from '@mui/icons-material';
import { collection, getDocs, doc, setDoc, updateDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../AuthContext';

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [openStepDialog, setOpenStepDialog] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedStep, setSelectedStep] = useState(null);
  const [currentProjectId, setCurrentProjectId] = useState(null);
  const { currentUser } = useAuth();

  // État du formulaire pour un projet
  const [projectForm, setProjectForm] = useState({
    title: '',
    order: 1,
    details: {
      participants: 0,
      keyBenefits: ''
    }
  });

  // État du formulaire pour une étape
  const [stepForm, setStepForm] = useState({
    title: '',
    date: '',
    status: 'à venir',
    progress: 0,
    substeps: []
  });

  // État du formulaire pour une sous-étape
  const [subStepForm, setSubStepForm] = useState({
    title: '',
    status: 'pas commencé'
  });
  
  // État pour le dialogue de sous-étape
  const [openSubStepDialog, setOpenSubStepDialog] = useState(false);
  const [selectedSubStep, setSelectedSubStep] = useState(null);
  const [currentStepId, setCurrentStepId] = useState(null);

  // Charger les projets depuis Firestore
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);
        const projectsSnapshot = await getDocs(collection(db, 'projects'));
        const projectsList = [];

        for (const projectDoc of projectsSnapshot.docs) {
          const projectData = projectDoc.data();
          const stepsSnapshot = await getDocs(collection(db, 'projects', projectDoc.id, 'steps'));
          
          const steps = stepsSnapshot.docs.map(stepDoc => ({
            id: stepDoc.id,
            ...stepDoc.data()
          }));

          projectsList.push({
            id: projectDoc.id,
            ...projectData,
            order: projectData.order || 999, // Valeur par défaut élevée pour les projets sans ordre
            steps: steps
          });
        }
        
        // Trier les projets par ordre croissant
        projectsList.sort((a, b) => a.order - b.order);

        setProjects(projectsList);
      } catch (err) {
        console.error('Erreur lors du chargement des projets:', err);
        setError('Impossible de charger les projets. ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  // Calculer la progression globale d'un projet
  const calculateOverallProgress = (steps) => {
    if (!steps || steps.length === 0) return 0;
    const totalProgress = steps.reduce((acc, step) => acc + step.progress, 0);
    return steps.length > 0 ? totalProgress / steps.length : 0;
  };

  // Ouvrir le dialogue pour ajouter/modifier un projet
  const handleOpenProjectDialog = (project = null) => {
    if (project) {
      setSelectedProject(project);
      setProjectForm({
        title: project.title || '',
        order: project.order || 1,
        details: {
          participants: project.details?.participants || 0,
          keyBenefits: project.details?.keyBenefits || ''
        }
      });
    } else {
      // Pour un nouveau projet, trouver l'ordre le plus élevé et ajouter 1
      const maxOrder = projects.length > 0 ? Math.max(...projects.map(p => p.order || 0)) : 0;
      setSelectedProject(null);
      setProjectForm({
        title: '',
        order: maxOrder + 1,
        details: {
          participants: 0,
          keyBenefits: ''
        }
      });
    }
    setOpenDialog(true);
  };

  // Ouvrir le dialogue pour ajouter/modifier une étape
  const handleOpenStepDialog = (projectId, step = null) => {
    setCurrentProjectId(projectId);
    if (step) {
      setSelectedStep(step);
      setStepForm({
        title: step.title || '',
        date: step.date || '',
        status: step.status || 'à venir',
        progress: step.progress || 0,
        substeps: step.substeps || []
      });
    } else {
      setSelectedStep(null);
      setStepForm({
        title: '',
        date: '',
        status: 'à venir',
        progress: 0,
        substeps: []
      });
    }
    setOpenStepDialog(true);
  };

  // Ouvrir le dialogue pour ajouter/modifier une sous-étape
  const handleOpenSubStepDialog = (stepId, subStep = null) => {
    setCurrentStepId(stepId);
    if (subStep) {
      setSelectedSubStep(subStep);
      setSubStepForm({
        title: subStep.title || '',
        status: subStep.status || 'pas commencé'
      });
    } else {
      setSelectedSubStep(null);
      setSubStepForm({
        title: '',
        status: 'pas commencé'
      });
    }
    setOpenSubStepDialog(true);
  };

  // Gérer les changements dans le formulaire de projet
  const handleProjectFormChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('details.')) {
      const detailField = name.split('.')[1];
      setProjectForm(prev => ({
        ...prev,
        details: {
          ...prev.details,
          [detailField]: detailField === 'participants' ? parseInt(value) || 0 : value
        }
      }));
    } else {
      setProjectForm(prev => ({ ...prev, [name]: value }));
    }
  };

  // Gérer les changements dans le formulaire d'étape
  const handleStepFormChange = (e) => {
    const { name, value } = e.target;
    setStepForm(prev => ({
      ...prev,
      [name]: ['progress'].includes(name) ? parseFloat(value) || 0 : value
    }));
  };

  // Gérer les changements dans le formulaire de sous-étape
  const handleSubStepFormChange = (e) => {
    const { name, value } = e.target;
    setSubStepForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Sauvegarder un projet
  const handleSaveProject = async () => {
    try {
      setLoading(true);
      const projectData = {
        title: projectForm.title,
        order: parseInt(projectForm.order) || 1, // Assurer que l'ordre est un nombre
        details: projectForm.details,
        updatedAt: serverTimestamp()
      };

      if (!selectedProject) {
        // Ajouter un nouveau projet
        const newProjectRef = doc(collection(db, 'projects'));
        projectData.createdAt = serverTimestamp();
        await setDoc(newProjectRef, projectData);

        setProjects(prev => [...prev, { id: newProjectRef.id, ...projectData, steps: [] }]);
        setSuccessMessage('Projet ajouté avec succès.');
      } else {
        // Mettre à jour un projet existant
        await updateDoc(doc(db, 'projects', selectedProject.id), projectData);

        setProjects(prev => prev.map(p => 
          p.id === selectedProject.id ? { ...p, ...projectData } : p
        ));
        setSuccessMessage('Projet mis à jour avec succès.');
      }

      setOpenDialog(false);
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      console.error('Erreur lors de la sauvegarde du projet:', err);
      setError('Erreur lors de la sauvegarde du projet. ' + err.message);
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  // Sauvegarder une étape
  const handleSaveStep = async () => {
    try {
      setLoading(true);
      const stepData = {
        title: stepForm.title,
        date: stepForm.date,
        status: stepForm.status,
        progress: parseFloat(stepForm.progress),
        substeps: stepForm.substeps || [],
        updatedAt: serverTimestamp()
      };

      if (!selectedStep) {
        // Ajouter une nouvelle étape
        const newStepRef = doc(collection(db, 'projects', currentProjectId, 'steps'));
        stepData.createdAt = serverTimestamp();
        await setDoc(newStepRef, stepData);

        setProjects(prev => prev.map(p => {
          if (p.id === currentProjectId) {
            return {
              ...p,
              steps: [...(p.steps || []), { id: newStepRef.id, ...stepData }]
            };
          }
          return p;
        }));
        setSuccessMessage('Étape ajoutée avec succès.');
      } else {
        // Mettre à jour une étape existante
        await updateDoc(doc(db, 'projects', currentProjectId, 'steps', selectedStep.id), stepData);

        setProjects(prev => prev.map(p => {
          if (p.id === currentProjectId) {
            return {
              ...p,
              steps: p.steps.map(s => s.id === selectedStep.id ? { ...s, ...stepData } : s)
            };
          }
          return p;
        }));
        setSuccessMessage('Étape mise à jour avec succès.');
      }

      setOpenStepDialog(false);
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      console.error('Erreur lors de la sauvegarde de l\'étape:', err);
      setError('Erreur lors de la sauvegarde de l\'étape. ' + err.message);
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  // Sauvegarder une sous-étape
  const handleSaveSubStep = () => {
    try {
      setLoading(true);
      const subStepData = {
        title: subStepForm.title,
        status: subStepForm.status,
        id: selectedSubStep ? selectedSubStep.id : Date.now().toString()
      };

      // Mettre à jour l'état local des sous-étapes dans le formulaire d'étape
      if (selectedSubStep) {
        // Modifier une sous-étape existante
        setStepForm(prev => ({
          ...prev,
          substeps: prev.substeps.map(ss => 
            ss.id === selectedSubStep.id ? subStepData : ss
          )
        }));
      } else {
        // Ajouter une nouvelle sous-étape
        setStepForm(prev => ({
          ...prev,
          substeps: [...prev.substeps, subStepData]
        }));
      }

      setOpenSubStepDialog(false);
      setSuccessMessage(selectedSubStep ? 'Sous-étape modifiée avec succès.' : 'Sous-étape ajoutée avec succès.');
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      console.error('Erreur lors de la sauvegarde de la sous-étape:', err);
      setError('Erreur lors de la sauvegarde de la sous-étape. ' + err.message);
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  // Supprimer un projet
  const handleDeleteProject = async (projectId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce projet et toutes ses étapes ?')) {
      return;
    }

    try {
      setLoading(true);
      
      // Supprimer toutes les étapes du projet
      const stepsSnapshot = await getDocs(collection(db, 'projects', projectId, 'steps'));
      const deleteStepsPromises = stepsSnapshot.docs.map(stepDoc => 
        deleteDoc(doc(db, 'projects', projectId, 'steps', stepDoc.id))
      );
      await Promise.all(deleteStepsPromises);
      
      // Supprimer le projet
      await deleteDoc(doc(db, 'projects', projectId));

      setProjects(prev => prev.filter(p => p.id !== projectId));
      setSuccessMessage('Projet supprimé avec succès.');
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      console.error('Erreur lors de la suppression du projet:', err);
      setError('Erreur lors de la suppression du projet. ' + err.message);
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  // Supprimer une étape
  const handleDeleteStep = async (projectId, stepId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette étape ?')) {
      return;
    }

    try {
      setLoading(true);
      await deleteDoc(doc(db, 'projects', projectId, 'steps', stepId));

      setProjects(prev => prev.map(p => {
        if (p.id === projectId) {
          return {
            ...p,
            steps: p.steps.filter(s => s.id !== stepId)
          };
        }
        return p;
      }));
      setSuccessMessage('Étape supprimée avec succès.');
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      console.error('Erreur lors de la suppression de l\'étape:', err);
      setError('Erreur lors de la suppression de l\'étape. ' + err.message);
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  // Supprimer une sous-étape
  const handleDeleteSubStep = (subStepId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette sous-étape ?')) {
      return;
    }

    try {
      setStepForm(prev => ({
        ...prev,
        substeps: prev.substeps.filter(ss => ss.id !== subStepId)
      }));
      setSuccessMessage('Sous-étape supprimée avec succès.');
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      console.error('Erreur lors de la suppression de la sous-étape:', err);
      setError('Erreur lors de la suppression de la sous-étape. ' + err.message);
      setTimeout(() => setError(''), 5000);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold', color: '#014527' }}>
          Gestion des Projets
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenProjectDialog()}
          sx={{ backgroundColor: '#014527', '&:hover': { backgroundColor: '#01331d' } }}
        >
          Nouveau Projet
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {projects.length === 0 && !loading ? (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="body1" align="center">
              Aucun projet n'a été créé. Cliquez sur "Nouveau Projet" pour commencer.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {projects.map((project) => {
            const overallProgress = calculateOverallProgress(project.steps);
            return (
              <Grid item xs={12} key={project.id}>
                <Card sx={{ mb: 2 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {project.title}
                      </Typography>
                      <Box>
                        <Tooltip title="Modifier le projet">
                          <IconButton onClick={() => handleOpenProjectDialog(project)} size="small">
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Supprimer le projet">
                          <IconButton onClick={() => handleDeleteProject(project.id)} size="small" color="error">
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>

                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="textSecondary">
                          <strong>Participants:</strong> {project.details?.participants || 0}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="textSecondary">
                          <strong>Atouts:</strong> {project.details?.keyBenefits || 'Non spécifié'}
                        </Typography>
                      </Grid>
                    </Grid>

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Progression globale: {overallProgress.toFixed(1)}%
                      </Typography>
                      <LinearProgress 
                        variant="determinate" 
                        value={overallProgress} 
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          backgroundColor: '#e2e8f0',
                          '.MuiLinearProgress-bar': {
                            backgroundColor: '#16a34a'
                          }
                        }}
                      />
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        Étapes du projet
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AddStepIcon />}
                        onClick={() => handleOpenStepDialog(project.id)}
                        sx={{ color: '#014527', borderColor: '#014527' }}
                      >
                        Ajouter une étape
                      </Button>
                    </Box>

                    {project.steps && project.steps.length > 0 ? (
                      project.steps.map((step) => (
                        <Accordion key={step.id} sx={{ mb: 1 }}>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography>{step.title}</Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Grid container spacing={2}>
                              <Grid item xs={12} sm={6}>
                                <Typography variant="body2">
                                  <strong>Date:</strong> {step.date}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Typography variant="body2">
                                  <strong>Statut:</strong> {step.status}
                                </Typography>
                              </Grid>
                              <Grid item xs={12}>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                  <strong>Progression:</strong> {step.progress}%
                                </Typography>
                                <LinearProgress 
                                  variant="determinate" 
                                  value={step.progress} 
                                  sx={{
                                    height: 8,
                                    borderRadius: 4,
                                    backgroundColor: '#e2e8f0',
                                    '.MuiLinearProgress-bar': {
                                      backgroundColor: '#16a34a'
                                    }
                                  }}
                                />
                              </Grid>
                              <Grid item xs={12}>
                                <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
                                  Sous-étapes:
                                </Typography>
                                {step.substeps && step.substeps.length > 0 ? (
                                  <Box sx={{ pl: 2 }}>
                                    {step.substeps.map((substep, idx) => (
                                      <Box key={substep.id || idx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                        <Typography variant="body2">
                                          {substep.title} - 
                                          <span style={{ 
                                            color: substep.status === 'réalisé' ? '#16a34a' : 
                                                  substep.status === 'en cours' ? '#2563eb' : 
                                                  '#94a3b8'
                                          }}>
                                            {substep.status}
                                          </span>
                                        </Typography>
                                        <Box>
                                          <IconButton 
                                            size="small" 
                                            onClick={() => handleOpenSubStepDialog(step.id, substep)}
                                          >
                                            <EditIcon fontSize="small" />
                                          </IconButton>
                                          <IconButton 
                                            size="small" 
                                            color="error" 
                                            onClick={() => handleDeleteSubStep(substep.id)}
                                          >
                                            <DeleteIcon fontSize="small" />
                                          </IconButton>
                                        </Box>
                                      </Box>
                                    ))}
                                  </Box>
                                ) : (
                                  <Typography variant="body2" color="textSecondary">
                                    Aucune sous-étape définie.
                                  </Typography>
                                )}
                                <Button
                                  size="small"
                                  startIcon={<AddIcon />}
                                  onClick={() => handleOpenSubStepDialog(step.id)}
                                  sx={{ mt: 1 }}
                                >
                                  Ajouter une sous-étape
                                </Button>
                              </Grid>
                              <Grid item xs={12} sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                  size="small"
                                  startIcon={<EditIcon />}
                                  onClick={() => handleOpenStepDialog(project.id, step)}
                                  sx={{ mr: 1 }}
                                >
                                  Modifier
                                </Button>
                                <Button
                                  size="small"
                                  color="error"
                                  startIcon={<DeleteIcon />}
                                  onClick={() => handleDeleteStep(project.id, step.id)}
                                >
                                  Supprimer
                                </Button>
                              </Grid>
                            </Grid>
                          </AccordionDetails>
                        </Accordion>
                      ))
                    ) : (
                      <Typography variant="body2" color="textSecondary" align="center">
                        Aucune étape définie pour ce projet.
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Dialogue pour ajouter/modifier un projet */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedProject ? 'Modifier le projet' : 'Ajouter un nouveau projet'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="title"
            label="Titre du projet"
            type="text"
            fullWidth
            variant="outlined"
            value={projectForm.title}
            onChange={handleProjectFormChange}
            required
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            margin="dense"
            name="order"
            label="Ordre d'affichage (1 = premier)"
            type="number"
            fullWidth
            variant="outlined"
            value={projectForm.order}
            onChange={handleProjectFormChange}
            helperText="Définit l'ordre d'affichage des projets (1 sera affiché en premier)"
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            name="details.participants"
            label="Nombre de participants"
            type="number"
            fullWidth
            variant="outlined"
            value={projectForm.details.participants}
            onChange={handleProjectFormChange}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            name="details.keyBenefits"
            label="Atouts du projet"
            type="text"
            fullWidth
            variant="outlined"
            value={projectForm.details.keyBenefits}
            onChange={handleProjectFormChange}
            multiline
            rows={3}
            sx={{ mb: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Annuler</Button>
          <Button 
            onClick={handleSaveProject} 
            variant="contained"
            disabled={!projectForm.title}
            sx={{ backgroundColor: '#014527', '&:hover': { backgroundColor: '#01331d' } }}
          >
            {loading ? <CircularProgress size={24} /> : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialogue pour ajouter/modifier une étape */}
      <Dialog open={openStepDialog} onClose={() => setOpenStepDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedStep ? 'Modifier l\'étape' : 'Ajouter une nouvelle étape'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="title"
            label="Titre de l'étape"
            type="text"
            fullWidth
            variant="outlined"
            value={stepForm.title}
            onChange={handleStepFormChange}
            required
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            margin="dense"
            name="date"
            label="Période (ex: 01/01/2025 - 31/03/2025)"
            type="text"
            fullWidth
            variant="outlined"
            value={stepForm.date}
            onChange={handleStepFormChange}
            sx={{ mb: 2 }}
          />
          <TextField
            select
            margin="dense"
            name="status"
            label="Statut"
            fullWidth
            variant="outlined"
            value={stepForm.status}
            onChange={handleStepFormChange}
            sx={{ mb: 2 }}
            SelectProps={{
              native: true,
            }}
          >
            <option value="à venir">À venir</option>
            <option value="en-cours">En cours</option>
            <option value="terminé">Terminé</option>
          </TextField>
          <TextField
            margin="dense"
            name="progress"
            label="Progression (%)"
            type="number"
            fullWidth
            variant="outlined"
            value={stepForm.progress}
            onChange={handleStepFormChange}
            sx={{ mb: 2 }}
          />
          
          {/* Liste des sous-étapes */}
          {stepForm.substeps && stepForm.substeps.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Sous-étapes:
              </Typography>
              {stepForm.substeps.map((substep, idx) => (
                <Box key={substep.id || idx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                  <Typography variant="body2">
                    {substep.title} - 
                    <span style={{ 
                      color: substep.status === 'réalisé' ? '#16a34a' : 
                            substep.status === 'en cours' ? '#2563eb' : 
                            '#94a3b8'
                    }}>
                      {substep.status}
                    </span>
                  </Typography>
                  <Box>
                    <IconButton 
                      size="small" 
                      onClick={() => handleOpenSubStepDialog(null, substep)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      color="error" 
                      onClick={() => handleDeleteSubStep(substep.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
          
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => handleOpenSubStepDialog(null)}
            sx={{ mb: 2 }}
          >
            Ajouter une sous-étape
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenStepDialog(false)}>Annuler</Button>
          <Button 
            onClick={handleSaveStep} 
            variant="contained"
            disabled={!stepForm.title}
            sx={{ backgroundColor: '#014527', '&:hover': { backgroundColor: '#01331d' } }}
          >
            {loading ? <CircularProgress size={24} /> : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Dialogue pour ajouter/modifier une sous-étape */}
      <Dialog open={openSubStepDialog} onClose={() => setOpenSubStepDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {selectedSubStep ? 'Modifier la sous-étape' : 'Ajouter une nouvelle sous-étape'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="title"
            label="Titre de la sous-étape"
            type="text"
            fullWidth
            variant="outlined"
            value={subStepForm.title}
            onChange={handleSubStepFormChange}
            required
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            select
            margin="dense"
            name="status"
            label="Statut"
            fullWidth
            variant="outlined"
            value={subStepForm.status}
            onChange={handleSubStepFormChange}
            sx={{ mb: 1 }}
            SelectProps={{
              native: true,
            }}
          >
            <option value="pas commencé">Pas commencé</option>
            <option value="en cours">En cours</option>
            <option value="réalisé">Réalisé</option>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenSubStepDialog(false)}>Annuler</Button>
          <Button 
            onClick={handleSaveSubStep} 
            variant="contained"
            disabled={!subStepForm.title}
            sx={{ backgroundColor: '#014527', '&:hover': { backgroundColor: '#01331d' } }}
          >
            {loading ? <CircularProgress size={24} /> : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Projects;